import { useTranslation } from 'react-i18next';

export default function ContractDetailsSection() {
  const { t } = useTranslation();

  const contractDetails = [
    {
      title: t('markets.contractSections.contractSize.title'),
      icon: 'ri-scales-3-line',
      details: [
        {
          label: t('markets.contractSections.contractSize.items.xauusd.label'),
          value: t("markets.contract.value.goldSize", { defaultValue: "100  ounces" }),
          description: t('markets.contractSections.contractSize.items.xauusd.desc'),
        },
        {
          label: t('markets.contractSections.contractSize.items.xagusd.label'),
          value: t("markets.contract.value.silverSize", { defaultValue: "5,000  ounces" }),
          description: t('markets.contractSections.contractSize.items.xagusd.desc'),
        },
        {
          label: t('markets.contractSections.contractSize.items.gaucnh.label'),
          value: '1,000 gau',
          description: t('markets.contractSections.contractSize.items.gaucnh.desc'),
        },
      ],
    },
    {
      title: t('markets.contractSections.priceMove.title'),
      icon: 'ri-arrow-up-down-line',
      details: [
        {
          label: t('markets.contractSections.priceMove.items.goldTick.label'),
          value: '0.01 USD',
          description: t('markets.contractSections.priceMove.items.goldTick.desc'),
        },
        {
          label: t('markets.contractSections.priceMove.items.silverTick.label'),
          value: '0.001 USD',
          description: t('markets.contractSections.priceMove.items.silverTick.desc'),
        },
        {
          label: t('markets.contractSections.contractSize.items.gaucnh.label'),
          value: '0.01',
          description: t('markets.contractSections.priceMove.items.fxTick.desc'),
        },
      ],
    },
    {
      title: t('markets.contractSections.settlement.title'),
      icon: 'ri-exchange-line',
      details: [
        {
          label: t('markets.contractSections.settlement.items.type.label'),
          value: t('markets.contractSections.settlement.items.type.value'),
          description: t('markets.contractSections.settlement.items.type.desc'),
        },
        {
          label: t('markets.contractSections.settlement.items.currency.label'),
          value: 'USD',
          description: t('markets.contractSections.settlement.items.currency.desc'),
        },
        {
          label: t('markets.contractSections.settlement.items.fxQuote.label'),
          value: 'CNH',
          description: t('markets.contractSections.settlement.items.fxQuote.desc'),
        },
      ],
    },
    {
      title: t('markets.contractSections.positionLimits.title'),
      icon: 'ri-shield-check-line',
      details: [
        {
          label: t('markets.contractSections.positionLimits.items.maxPosition.label'),
          value: t("markets.contract.value.maxPosition", { defaultValue: "1,000 lots" }),
          description: t('markets.contractSections.positionLimits.items.maxPosition.desc'),
        },
        {
          label: t('markets.contractSections.positionLimits.items.orderDistance.label'),
          value: t("markets.contract.value.orderDistance", { defaultValue: "50 points" }),
          description: t('markets.contractSections.positionLimits.items.orderDistance.desc'),
        },
        {
          label: t('markets.contractSections.positionLimits.items.execution.label'),
          value: t('markets.contractSections.positionLimits.items.execution.value'),
          description: t('markets.contractSections.positionLimits.items.execution.desc'),
        },
      ],
    },
  ];

  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white" id="contract-details">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            {t('markets.contractDetials')}
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
            {t('markets.contractDetials.desc')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {contractDetails.map((section, index) => (
            <div
              key={index}
              className="bg-white rounded-3xl p-6 md:p-8 border border-gray-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-r from-yellow-400/20 to-yellow-600/20 rounded-2xl flex items-center justify-center border border-yellow-400/40 mr-4">
                  <i className={`${section.icon} text-2xl text-yellow-600`}></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">{section.title}</h3>
              </div>

              <div className="space-y-4">
                {section.details.map((detail, detailIndex) => (
                  <div key={detailIndex} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex justify-between items-start gap-4 mb-2">
                      <span className="text-sm font-medium text-gray-600">{detail.label}</span>
                      <span className="text-sm md:text-lg font-bold text-gray-900 text-right">
                        {detail.value}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{detail.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-6 md:p-8 border border-blue-200">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <i className="ri-calculator-line text-blue-600 mr-3"></i>
            {t('markets.tradingExample')}
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-5 md:p-6 border border-blue-100">
              <h4 className="text-lg font-bold text-gray-900 mb-4">XAUUSD</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.instrument')}:</span>
                  <span className="font-semibold">XAUUSD</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.lotSize')}:</span>
                  <span className="font-semibold">1.0 lot (100 oz)</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.entPrice')}:</span>
                  <span className="font-semibold">$5,000.00</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.marReq')}:</span>
                  <span className="font-semibold text-blue-600">$5,000.00</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 md:p-6 border border-blue-100">
              <h4 className="text-lg font-bold text-gray-900 mb-4">XAGUSD</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.instrument')}:</span>
                  <span className="font-semibold">XAGUSD</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.lotSize')}:</span>
                  <span className="font-semibold">1.0 lot (5,000 oz)</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.entPrice')}:</span>
                  <span className="font-semibold">$70.50</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.marReq')}:</span>
                  <span className="font-semibold text-blue-600">$17,625.00</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 md:p-6 border border-blue-100">
              <h4 className="text-lg font-bold text-gray-900 mb-4">GAUCNH</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.instrument')}:</span>
                  <span className="font-semibold">GAUCNH</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.lotSize')}:</span>
                  <span className="font-semibold">1.0 lot (1,000 gau)</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.entPrice')}:</span>
                  <span className="font-semibold">$1,100</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">{t('markets.marReq')}:</span>
                  <span className="font-semibold text-blue-600">~$3,000</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
